import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Wallet, Lock, TrendingUp, TrendingDown, Zap,
  ChevronDown, ChevronUp, Info, RefreshCw, Award
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine, Area, AreaChart
} from 'recharts';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const TIER_COLORS = {
  Elite:      '#f59e0b',
  Advanced:   '#6366f1',
  Standard:   '#16C784',
  Restricted: '#f97316',
  Minimum:    '#ef4444',
};
const TIERS = [
  { label: 'Minimum',    capital: 50000,  color: '#ef4444' },
  { label: 'Restricted', capital: 75000,  color: '#f97316' },
  { label: 'Standard',   capital: 100000, color: '#16C784' },
  { label: 'Advanced',   capital: 150000, color: '#6366f1' },
  { label: 'Elite',      capital: 200000, color: '#f59e0b' },
];
const BUCKET_COLORS = {
  pre_trade:  '#6366f1',
  execution:  '#16C784',
  risk:       '#f59e0b',
  outcome:    '#38bdf8',
};

function fmt(n) {
  if (n >= 100000) return `₹${(n / 100000).toFixed(1)}L`;
  if (n >= 1000)   return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

function GradientBar({ value, max = 100, label, sublabel, color }) {
  const { colors } = useTheme();
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <span className="text-[12px]" style={{ color: colors.secondaryText }}>{label}</span>
        {sublabel && <span className="text-[12px] font-semibold tabular-nums" style={{ color: colors.primaryText }}>{sublabel}</span>}
      </div>
      <div className="relative h-2 rounded-full overflow-visible"
        style={{ background: 'linear-gradient(to right, #ef4444 0%, #f97316 25%, #f59e0b 50%, #84cc16 75%, #16C784 100%)' }}
      >
        <div
          className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full border-2 border-white shadow"
          style={{ left: `calc(${pct}% - 7px)`, backgroundColor: '#fff', transition: 'left 0.5s ease' }}
        />
      </div>
    </div>
  );
}

function ScoreRing({ score, size = 72, trackColor }) {
  const scoreColor =
    score >= 65 ? '#16C784' :
    score >= 50 ? '#6366f1' :
    '#ef4444';
  const r = (size / 2) - 6;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  return (
    <div className="relative flex items-center justify-center flex-shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size/2} cy={size/2} r={r} strokeWidth="5" fill="none" stroke={trackColor || 'rgba(255,255,255,0.07)'} />
        <circle
          cx={size/2} cy={size/2} r={r} strokeWidth="5" fill="none"
          style={{
            stroke: scoreColor,
            strokeDasharray: `${dash} ${circ}`,
            strokeLinecap: 'round',
            transition: 'stroke-dasharray 0.8s ease',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[20px] font-bold tabular-nums leading-none" style={{ color: scoreColor }}>{score}</span>
        <span className="text-[9px] mt-0.5" style={{ color: scoreColor, opacity: 0.7 }}>score</span>
      </div>
    </div>
  );
}

function ComponentBar({ label, value }) {
  const { colors } = useTheme();
  const color = value >= 65 ? '#16C784' : value >= 50 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] flex-shrink-0 w-36 truncate" style={{ color: colors.secondaryText }}>{label}</span>
      <div className="flex-1 h-1.5 rounded-full" style={{ backgroundColor: colors.ringTrack }}>
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${value}%`, backgroundColor: color }} />
      </div>
      <span className="text-[11px] font-semibold tabular-nums w-7 text-right" style={{ color }}>{value}</span>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label, colors }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg px-3 py-2 shadow-lg text-[11px]"
      style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
      <p className="font-semibold mb-1" style={{ color: colors.primaryText }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {p.name === 'Score' ? p.value : fmt(p.value)}</p>
      ))}
    </div>
  );
};

export default function CapitalPage() {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { user } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus]   = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resetting, setResetting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [expandBuckets, setExpandBuckets] = useState({});
  const [chartMetric, setChartMetric] = useState('active_capital'); // 'active_capital' | 'score'

  const fetchAll = useCallback(async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API_URL}/capital/status`),
        axios.get(`${API_URL}/capital/history?days=30`),
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleReset = async () => {
    setResetting(true);
    try {
      await axios.post(`${API_URL}/capital/reset`);
      await fetchAll();
    } catch (e) { /* silent */ }
    finally { setResetting(false); }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 rounded-2xl animate-pulse" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)` }} />
        ))}
      </div>
    );
  }

  if (!status) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-3">
        <p className="text-[14px]" style={{ color: colors.secondaryText }}>Capital data unavailable</p>
        <button onClick={() => navigate(-1)} className="text-[13px]" style={{ color: colors.accent }}>← Go back</button>
      </div>
    );
  }

  const {
    total_capital, active_capital, locked_capital, available_capital,
    capital_behavior_score, bootstrap_mode, trades_until_live,
    tier, score_buckets, tomorrow, total_trades,
  } = status;

  const tierColor   = TIER_COLORS[tier] || colors.accent;
  const scoreColor  = capital_behavior_score >= 65 ? '#16C784' : capital_behavior_score >= 50 ? '#6366f1' : '#ef4444';
  const availablePct = active_capital > 0 ? Math.round((available_capital / active_capital) * 100) : 0;
  const currentTierIdx = TIERS.findIndex(t => t.label === tier || (tier === 'Minimum' && t.label === 'Minimum'));

  // Format history labels
  const chartData = history.map(h => ({
    ...h,
    label: new Date(h.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
  }));

  return (
    <div className="space-y-3 pb-6">

      {/* Back + title */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg" style={{ backgroundColor: `${colors.accent}18` }}>
          <ArrowLeft className="w-4 h-4" style={{ color: colors.accent }} />
        </button>
        <div className="flex-1">
          <h1 className="text-[17px] font-bold leading-tight" style={{ color: colors.primaryText }}>Capital Control</h1>
          <p className="text-[11px]" style={{ color: colors.secondaryText }}>Your behavior-based trading limit</p>
        </div>
        <button
          onClick={handleReset}
          disabled={resetting}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-semibold"
          style={{ backgroundColor: `${colors.accent}18`, color: colors.accent }}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${resetting ? 'animate-spin' : ''}`} />
          Recalculate
        </button>
      </div>

      {/* ── Hero card ───────────────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${tierColor}40` }}>
        <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            {/* Virtual balance */}
            <p className="text-[11px] uppercase tracking-wide font-medium mb-0.5" style={{ color: colors.secondaryText }}>Virtual Balance</p>
            <p className="text-[22px] font-bold tabular-nums leading-none mb-3" style={{ color: colors.profit }}>
              ₹{Number(user?.virtual_balance || total_capital).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>

            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: `${tierColor}20`, color: tierColor }}>{tier}</span>
              {bootstrap_mode && (
                <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${colors.accent}18`, color: colors.accent }}>
                  Bootstrap · {trades_until_live} trades left
                </span>
              )}
            </div>
            <p className="text-[11px] uppercase tracking-wide font-medium mt-3 mb-0.5" style={{ color: tierColor }}>Available Today</p>
            <p className="text-[28px] font-bold tabular-nums leading-none" style={{ color: tierColor }}>{fmt(available_capital)}</p>
            <p className="text-[11px] mt-0.5" style={{ color: colors.secondaryText }}>{availablePct}% of {fmt(active_capital)} daily limit</p>
          </div>

          {!bootstrap_mode && <ScoreRing score={capital_behavior_score} size={80} trackColor={colors.ringTrack} />}
        </div>

        {/* Three stat tiles */}
        <div className="grid grid-cols-3 gap-0" style={{ borderTop: `1px solid ${colors.border}` }}>
          {[
            { label: 'Total Capital', value: fmt(total_capital), sub: 'incl. P&L',             color: colors.primaryText },
            { label: 'Active Limit',  value: fmt(active_capital), sub: 'today\'s cap',          color: tierColor },
            { label: 'Used Today',    value: fmt(locked_capital), sub: 'new positions today',
              color: locked_capital > 0 ? '#f97316' : colors.secondaryText,
              icon: locked_capital > 0 ? Lock : null },
          ].map((s, i) => (
            <div key={s.label}
              className="px-3 py-2.5 flex flex-col"
              style={{ borderRight: i < 2 ? `1px solid ${colors.border}` : 'none' }}
            >
              <p className="text-[10px] uppercase tracking-wide mb-0.5" style={{ color: colors.secondaryText }}>{s.label}</p>
              <p className="text-[15px] font-bold tabular-nums" style={{ color: s.color }}>{s.value}</p>
              <p className="text-[10px]" style={{ color: colors.secondaryText }}>{s.sub}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Tier progression ──────────────────────────────────────────────── */}
      <div className="rounded-2xl px-4 py-3" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
        <p className="text-[12px] font-semibold uppercase tracking-wider mb-3" style={{ color: colors.secondaryText }}>Tier Progression</p>
        <div className="flex items-center justify-between">
          {TIERS.map((t, i) => {
            const active = t.label === tier || (tier === 'Minimum' && t.label === 'Minimum');
            const unlocked = i <= currentTierIdx;
            return (
              <React.Fragment key={t.label}>
                <div className="flex flex-col items-center gap-1">
                  <span className="text-[10px] tabular-nums" style={{ color: unlocked ? t.color : colors.secondaryText, opacity: unlocked ? 1 : 0.35, fontWeight: active ? 700 : 400 }}>
                    {fmt(t.capital)}
                  </span>
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{
                      backgroundColor: t.color,
                      opacity: unlocked ? 1 : 0.25,
                      boxShadow: active ? `0 0 8px ${t.color}` : 'none',
                      border: active ? `2px solid ${t.color}` : '2px solid transparent',
                    }}
                  />
                  <span className="text-[9px]" style={{ color: unlocked ? t.color : colors.secondaryText, opacity: unlocked ? 1 : 0.35, fontWeight: active ? 700 : 400 }}>
                    {t.label}
                  </span>
                </div>
                {i < TIERS.length - 1 && (
                  <div className="flex-1 h-px mx-1.5" style={{ backgroundColor: i < currentTierIdx ? tierColor : colors.border, opacity: i < currentTierIdx ? 0.6 : 1 }} />
                )}
              </React.Fragment>
            );
          })}
        </div>
        <p className="text-[11px] mt-3 text-center" style={{ color: colors.secondaryText }}>
          {capital_behavior_score >= 80 ? 'You\'ve reached Elite tier 🏆' :
           capital_behavior_score >= 65 ? `${80 - capital_behavior_score} score points to Elite tier` :
           capital_behavior_score >= 50 ? `${65 - capital_behavior_score} score points to Advanced tier` :
           `${50 - capital_behavior_score} score points to Standard tier`}
        </p>
      </div>

      {/* ── Capital bars ─────────────────────────────────────────────────── */}
      <div className="rounded-2xl px-4 py-3 space-y-4" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
        <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Capital Breakdown</p>
        <GradientBar label="Active Daily Limit"  value={active_capital}    max={200000}          sublabel={fmt(active_capital)} />
        <GradientBar label="Locked in Trades"    value={locked_capital}    max={active_capital || 1} sublabel={fmt(locked_capital)} />
        <GradientBar label="Available to Trade"  value={available_capital} max={active_capital || 1} sublabel={fmt(available_capital)} />
      </div>

      {/* ── Score breakdown ───────────────────────────────────────────────── */}
      {!bootstrap_mode && score_buckets && (
        <div className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
          <div className="px-4 pt-3 pb-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}` }}>
            <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Score Breakdown</p>
            <div className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: scoreColor }} />
              <span className="text-[13px] font-bold" style={{ color: scoreColor }}>{capital_behavior_score} / 100</span>
            </div>
          </div>
          <div className="p-3 grid grid-cols-2 gap-2">
            {Object.entries(score_buckets).map(([key, b]) => {
              const bColor  = BUCKET_COLORS[key] || colors.accent;
              const sColor  = b.score >= 65 ? '#16C784' : b.score >= 50 ? bColor : '#ef4444';
              const open    = expandBuckets[key];
              return (
                <div key={key}
                  className="rounded-xl overflow-hidden"
                  style={{ backgroundColor: `${sColor}0d`, border: `1px solid ${sColor}25` }}
                >
                  <button
                    className="w-full px-3 py-2 flex items-start justify-between"
                    onClick={() => setExpandBuckets(p => ({ ...p, [key]: !p[key] }))}
                  >
                    <div className="text-left">
                      <p className="text-[10px] font-medium leading-tight" style={{ color: colors.secondaryText }}>
                        {b.label.replace(' Planning','').replace(' Discipline','').replace(' Context','')}
                        <span className="ml-1" style={{ color: bColor }}>·{b.weight}%</span>
                      </p>
                      <p className="text-[24px] font-bold tabular-nums leading-none mt-0.5" style={{ color: sColor }}>{b.score}</p>
                    </div>
                    {open
                      ? <ChevronUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: colors.secondaryText }} />
                      : <ChevronDown className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: colors.secondaryText }} />
                    }
                  </button>
                  {open && (
                    <div className="px-3 pb-2.5 space-y-2" style={{ borderTop: `1px solid ${sColor}20` }}>
                      {Object.entries(b.components).map(([cLabel, cVal]) => (
                        <ComponentBar key={cLabel} label={cLabel} value={cVal} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Tomorrow's projection ────────────────────────────────────────── */}
      {tomorrow && (
        <div className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
          <div className="px-4 pt-3 pb-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}` }}>
            <div className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" style={{ color: colors.accent }} />
              <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>Tomorrow's Projection</p>
            </div>
            <div>
              <span
                className="text-[18px] font-bold tabular-nums"
                style={{ color: tomorrow.projected_capital >= active_capital ? '#16C784' : tomorrow.projected_capital < active_capital ? '#ef4444' : colors.accent }}
              >
                {fmt(tomorrow.projected_capital)}
              </span>
              {tomorrow.projected_capital !== active_capital && (
                <span className="text-[11px] ml-1.5 font-semibold"
                  style={{ color: tomorrow.projected_capital >= active_capital ? '#16C784' : '#ef4444' }}>
                  {tomorrow.projected_capital >= active_capital ? '▲' : '▼'} {fmt(Math.abs(tomorrow.projected_capital - active_capital))}
                </span>
              )}
            </div>
          </div>
          <div className="px-4 py-3">
            <p className="text-[11px] mb-2" style={{ color: colors.secondaryText }}>
              Resets at 9:00 AM based on today's behavior score ({tomorrow.score}).
            </p>
            {tomorrow.reasons?.length > 0 && (
              <ul className="space-y-1.5">
                {tomorrow.reasons.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[12px]" style={{ color: colors.primaryText }}>
                    <span style={{ color: colors.accent, flexShrink: 0 }}>·</span>
                    {r}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── History chart ─────────────────────────────────────────────────── */}
      <div className="rounded-2xl overflow-hidden" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
        <div className="px-4 pt-3 pb-2 flex items-center justify-between" style={{ borderBottom: `1px solid ${colors.border}` }}>
          <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>30-Day History</p>
          <div className="flex gap-1">
            {[
              { key: 'active_capital', label: 'Capital' },
              { key: 'score',          label: 'Score' },
            ].map(m => (
              <button key={m.key} onClick={() => setChartMetric(m.key)}
                className="px-2.5 py-1 rounded-md text-[11px] font-semibold"
                style={{
                  backgroundColor: chartMetric === m.key ? `${colors.accent}22` : 'transparent',
                  color: chartMetric === m.key ? colors.accent : colors.secondaryText,
                  border: chartMetric === m.key ? `1px solid ${colors.accent}40` : '1px solid transparent',
                }}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-2 py-3">
          {chartData.length <= 1 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2">
              <Zap className="w-8 h-8 opacity-20" style={{ color: colors.accent }} />
              <p className="text-[12px] text-center" style={{ color: colors.secondaryText }}>
                History builds after your first daily reset.<br />Recalculate to log today's snapshot.
              </p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="capGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor={colors.accent} stopOpacity={0.25} />
                    <stop offset="95%" stopColor={colors.accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.06)'} vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fill: colors.secondaryText }}
                  tickLine={false} axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 9, fill: colors.secondaryText }}
                  tickLine={false} axisLine={false}
                  tickFormatter={v => chartMetric === 'score' ? v : fmt(v)}
                  width={38}
                />
                <Tooltip content={<CustomTooltip colors={colors} />} />
                {chartMetric === 'score' && (
                  <>
                    <ReferenceLine y={65} stroke="#16C784" strokeDasharray="3 3" strokeOpacity={0.4} />
                    <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="3 3" strokeOpacity={0.4} />
                  </>
                )}
                <Area
                  type="monotone"
                  dataKey={chartMetric}
                  name={chartMetric === 'score' ? 'Score' : 'Active Capital'}
                  stroke={colors.accent}
                  fill="url(#capGrad)"
                  strokeWidth={2}
                  dot={chartData.length <= 10}
                  activeDot={{ r: 4, fill: colors.accent }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* History table */}
        {chartData.length > 1 && (
          <div style={{ borderTop: `1px solid ${colors.border}` }}>
            <div className="px-4 py-2 grid grid-cols-4 gap-2"
              style={{ backgroundColor: colors.inactiveBg, borderBottom: `1px solid ${colors.border}` }}>
              {['Date', 'Score', 'Limit', 'Tier'].map(h => (
                <span key={h} className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: colors.secondaryText }}>{h}</span>
              ))}
            </div>
            <div className="divide-y" style={{ borderColor: colors.border }}>
              {[...chartData].reverse().slice(0, 14).map((row, i) => {
                const tc = TIER_COLORS[row.tier] || colors.accent;
                const sc = row.score >= 65 ? '#16C784' : row.score >= 50 ? '#f59e0b' : '#ef4444';
                return (
                  <div key={i} className="px-4 py-2 grid grid-cols-4 gap-2 items-center"
                    style={{ backgroundColor: i % 2 === 0 ? 'transparent' : colors.inactiveBg }}>
                    <span className="text-[11px]" style={{ color: colors.secondaryText }}>{row.label}</span>
                    <span className="text-[12px] font-bold tabular-nums" style={{ color: sc }}>{row.score}</span>
                    <span className="text-[12px] font-semibold tabular-nums" style={{ color: colors.primaryText }}>{fmt(row.active_capital)}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold w-fit"
                      style={{ backgroundColor: `${tc}18`, color: tc }}>{row.tier}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Bootstrap info ───────────────────────────────────────────────── */}
      {bootstrap_mode && (
        <div className="rounded-2xl px-4 py-3 flex items-start gap-3"
          style={{ backgroundColor: `${colors.accent}0d`, border: `1px solid ${colors.accent}25` }}>
          <Zap className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: colors.accent }} />
          <div>
            <p className="text-[13px] font-semibold mb-1" style={{ color: colors.primaryText }}>Bootstrap Mode Active</p>
            <p className="text-[12px] leading-relaxed" style={{ color: colors.secondaryText }}>
              You're in the first {5} trades phase. Capital is fixed at ₹1L regardless of score.
              After <strong style={{ color: colors.accent }}>{trades_until_live} more trade{trades_until_live !== 1 ? 's' : ''}</strong>, behavior-based capital kicks in and your limit adjusts daily based on how disciplined you trade.
            </p>
          </div>
        </div>
      )}

      {/* ── How it works ──────────────────────────────────────────────────── */}
      <div className="rounded-2xl px-4 py-3" style={{ background: `linear-gradient(135deg, ${colors.accent}20 0%, ${colors.accent}08 100%)`, border: `1px solid ${colors.accentBorder}` }}>
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4" style={{ color: colors.secondaryText }} />
          <p className="text-[12px] font-semibold uppercase tracking-wider" style={{ color: colors.secondaryText }}>How It Works</p>
        </div>
        <div className="space-y-2.5">
          {[
            { icon: Award,      color: '#f59e0b', title: 'Behavior-based daily limit',     desc: 'Your active capital is recalculated every morning at 9 AM based on how disciplined you traded.' },
            { icon: Lock,       color: '#f97316', title: 'Locked = open trade value',       desc: 'When you enter a trade, that value is locked. It\'s released when the trade closes.' },
            { icon: TrendingUp, color: '#16C784', title: 'Trade well → unlock more capital', desc: 'Use stop losses, plan your trades, avoid overtrading. Score ≥65 puts you in Advanced or Elite.' },
          ].map(({ icon: Icon, color, title, desc }) => (
            <div key={title} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${color}18` }}>
                <Icon className="w-3.5 h-3.5" style={{ color }} />
              </div>
              <div>
                <p className="text-[12px] font-semibold" style={{ color: colors.primaryText }}>{title}</p>
                <p className="text-[11px] leading-relaxed mt-0.5" style={{ color: colors.secondaryText }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
